"use client";

import Link from "next/link";
import { ArrowRight, Scale, FileCheck, Eye } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { legal as data } from "@/lib/data/about";

const iconMap = { Scale, FileCheck, Eye } as const;
const accentMap = {
  crimson: "var(--color-crimson)",
  denim: "var(--color-denim)",
  emerald: "var(--color-emerald)",
} as const;

export function Legal() {
  return (
    <Section id="legal" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-denim)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-3 gap-5">
          {data.pillars.map((p, i) => {
            const Icon = iconMap[p.icon as keyof typeof iconMap] ?? Scale;
            const accent = accentMap[p.accent];
            return (
              <Reveal key={p.title} delay={i * 100}>
                <div
                  className="glass rounded-2xl p-6 h-full"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 12%, transparent)`,
                  }}
                >
                  <span
                    className="grid place-items-center h-11 w-11 rounded-xl"
                    style={{
                      background: `color-mix(in oklch, ${accent} 18%, transparent)`,
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)`,
                    }}
                  >
                    <Icon className="h-5 w-5" style={{ color: accent }} />
                  </span>
                  <h3 className="mt-4 text-lg">{p.title}</h3>
                  <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={400}>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link
                key={c.label}
                href={c.href}
                className={
                  i === 0
                    ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                    : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                }
              >
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
